import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Case from 'case';
import uniqid from 'uniqid';
import { Box, Text, Button } from 'grommet';
import { SimpleModal } from '@Components/Control';

const StyledButton = styled(Button)`
  text-align: start;
  background-color: var(--button-table-bg);
  border-radius: var(--app-radius);
  padding: ${({ small }) => (small ? '0.35rem 0.6rem' : '0.25rem 0.5rem')};
  width: fit-content;
  border: 1px solid transparent;
  cursor: pointer;

  &:hover {
    border: 1px solid var(--page-focus-highlight);
  }

  ${({ isOpen }) => isOpen && `
    border: 1px solid var(--page-focus-highlight);
  `}
`;

const MatchStatsDetails = ({
  small = false,
  statsData,
  textColor = 'var(--page-modal-text)',
  fallbackHeader = 'Match stats',
  buttonLabel = 'View stats',
  watermarkGroupingKey = null,
}) => {
  const [showModal, setShowModal] = React.useState(false);
  const CONFIDENCE_LEVELS = [
    [95, '(Near Duplicate)'],
    [90, '(Very Strong Match)'],
    [80, '(Strong Match)'],
    [70, '(Probable Match)'],
    [60, '(Weak Match)'],
  ];

  if (!statsData || typeof statsData !== 'object') {
    return (
      <Text size="0.9rem" color={textColor}>
        -
      </Text>
    );
  }

  const camelToLabel = (text) => {
    if (!text) return '';

    return text
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  const getConfidenceDisplay = (confidence) => {
    const value = Number(confidence);
    if (!Number.isFinite(value)) return '(N/A)';

    return (
      CONFIDENCE_LEVELS.find(([min]) => value >= min)?.[1]
      || '(Very Weak Match)'
    );
  };

  const segments = Array.isArray(statsData.segments) ? statsData.segments : [];
  const evidence = statsData.evidence && typeof statsData.evidence === 'object' ? statsData.evidence : null;
  const watermarkGroupingKeyCamel = Case.camel(watermarkGroupingKey);
  const watermarkValue = watermarkGroupingKey && statsData[watermarkGroupingKeyCamel] !== undefined
    ? statsData[watermarkGroupingKeyCamel]
    : null;

  return (
    <Box>
      <StyledButton
        onClick={() => setShowModal(true)}
        isOpen={showModal}
      >
        <Text size={small ? '1rem' : '0.9rem'} weight={500} color="var(--button-table-text)">
          {buttonLabel}
        </Text>
      </StyledButton>
      {showModal && (
        <SimpleModal
          hideLowerSection
          small={small}
          toggleModal={setShowModal}
          modalTitle={fallbackHeader}
          titleSize="1.2rem"
          desktopMinWidth="30rem"
        >
          <Box
            direction="column"
            gap="0.75rem"
            pad={{ vertical: '0.5rem' }}
          >
            {statsData.matchType && (
              <Box direction="row" gap="0.5rem" align="center">
                <Text size="1rem" color={textColor} weight={500}>Match Type:</Text>
                <Text color={textColor} size="1rem">{statsData.matchType}</Text>
              </Box>
            )}
            {watermarkValue != null && (
              <Box direction="row" gap="0.5rem" align="center">
                <Text size="1rem" color={textColor} weight={500}>
                  {camelToLabel(watermarkGroupingKeyCamel)}
                  :
                </Text>
                <Text color={textColor} size="1rem">{String(watermarkValue)}</Text>
              </Box>
            )}
            {typeof statsData.confidence === 'number' && (
              <Box direction="row" gap="0.5rem" align="center">
                <Text size="1rem" color={textColor} weight={500}>Confidence:</Text>
                <Text color={textColor} size="1rem">
                  {statsData.confidence.toFixed(2)}
                  %
                  {' '}
                  {getConfidenceDisplay(statsData.confidence)}
                </Text>
              </Box>
            )}
            {evidence && (
              <Box direction="column" gap="0.25rem">
                <Text size="1rem" color={textColor} weight={500}>Evidence:</Text>
                {Object.entries(evidence).map(([key, value]) => (
                  <Box
                    direction="row"
                    gap="0.5rem"
                    align="start"
                    key={key}
                    pad={{ horizontal: '1rem' }}
                    wrap
                  >
                    <Text size="1rem" color={textColor} weight={500}>
                      {camelToLabel(key)}
                      :
                    </Text>
                    <Text color={textColor} size="1rem" style={{ wordBreak: 'break-word' }}>
                      {value ? String(value) : 'N/A'}
                    </Text>
                  </Box>
                ))}
              </Box>
            )}
            {segments.length > 0 && (
            <Box
              direction="column"
              gap="0.5rem"
              style={{ maxHeight: '42vh', overflowY: 'auto' }}
            >
              <Text size="1rem" color={textColor} weight={500}>
                Segments:
                {' ('}
                {segments.length}
                {')'}
              </Text>
              {segments.map((segment, idx) => (
                <Box
                  direction="column"
                  gap="0.15rem"
                  key={uniqid()}
                  pad={{ horizontal: '1rem', vertical: '0.35rem' }}
                  border={{ color: 'var(--nav-border)', side: 'bottom' }}
                  flex={false}
                >
                  <Text size="1rem" color={textColor} weight={500}>
                    Segment
                    {' '}
                    {idx + 1}
                  </Text>
                  <Box direction="column" gap="0.15rem" pad={{ horizontal: '1rem' }}>
                    <Text color={textColor} size="1rem">
                      Source:
                      {' '}
                      {segment.sourceStart}
                      {' '}
                      -
                      {' '}
                      {segment.sourceEnd}
                    </Text>
                    <Text color={textColor} size="1rem">
                      Copied:
                      {' '}
                      {segment.copiedStart}
                      {' '}
                      -
                      {' '}
                      {segment.copiedEnd}
                    </Text>
                    <Text color={textColor} size="1rem">
                      Type:
                      {' '}
                      {segment.matchType || 'Unknown'}
                    </Text>
                  </Box>
                </Box>
              ))}
            </Box>
            )}
          </Box>
        </SimpleModal>
      )}
    </Box>
  );
};

MatchStatsDetails.propTypes = {
  small: PropTypes.bool,
  statsData: PropTypes.shape({
    matchType: PropTypes.string,
    confidence: PropTypes.number,
    evidence: PropTypes.object,
    segments: PropTypes.arrayOf(PropTypes.shape({
      sourceStart: PropTypes.string,
      sourceEnd: PropTypes.string,
      copiedStart: PropTypes.string,
      copiedEnd: PropTypes.string,
      matchType: PropTypes.string,
    })),
  }),
  fallbackHeader: PropTypes.string,
  buttonLabel: PropTypes.string,
  textColor: PropTypes.string,
  watermarkGroupingKey: PropTypes.string,
};

export default MatchStatsDetails;
